import { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import NiceModal from '@ebay/nice-modal-react';
import { enqueueSnackbar } from 'notistack';

import { confirmSuccess, logoutSuccess } from '@store/authSlice';
import { tokenStorage } from '@utils/tokenStorage';
import { UpgradeRequiredModalId } from '@components/UpgradeRequiredModal';
import { refreshAccessToken } from './authRefresh';

/**
 * Wraps any RTK `fetchBaseQuery` with the app's unified auth + error handling so every
 * client (Django base, email-service direct, Django→email proxy) behaves identically:
 *   - 401  → refresh once (shared in-flight refresh) and retry; on failure, log out.
 *   - 403 TIER_LIMIT_EXCEEDED → open the upgrade modal.
 *   - 500 / 503 / network → a single de-duplicated snackbar.
 * Replaces three divergent copies that had drifted (missing logout, missing toasts, etc.).
 */
export const withReauth =
  (baseQuery: BaseQueryFn): BaseQueryFn =>
  async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);

    if (!result?.error) {
      // Successfully authenticated
      api.dispatch(confirmSuccess());
      return result;
    }

    const err = result.error as any;
    const status = err.status;
    const originalStatus = err.originalStatus;

    if (status === 403) {
      const errorData = err.data as any;
      if (errorData?.code === 'TIER_LIMIT_EXCEEDED') {
        NiceModal.show(UpgradeRequiredModalId, {
          feature: errorData.feature,
          message: errorData.message,
          currentUsage: errorData.currentUsage,
          limit: errorData.limit,
          plan: errorData.plan,
          requiredPlan: errorData.requiredPlan,
        });
      }
    } else if (status === 401 || originalStatus === 401) {
      if (await refreshAccessToken()) {
        result = await baseQuery(args, api, extraOptions);
      } else {
        tokenStorage.cleanup();
        api.dispatch(logoutSuccess());
      }
    } else if (status === 500) {
      enqueueSnackbar('Something went wrong. Please try again later.', { variant: 'error', preventDuplicate: true });
    } else if (status === 503) {
      enqueueSnackbar('Service temporarily unavailable. Please try again shortly.', { variant: 'warning', preventDuplicate: true });
    } else if (status === 'FETCH_ERROR') {
      enqueueSnackbar('Connection lost. Please check your network.', { variant: 'error', preventDuplicate: true });
    }

    return result;
  };
